import { defineComponent as _defineComponent } from 'vue'
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

import NewDesignModal from '@/components/modals/vfm/NewDesignModal.vue';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { computed, ref, watch } from 'vue';
import type { TrailerClip } from '@/interfaces/trailerClip';
import { getTrailerComponent } from '@/helpers/trailer-helper';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import { SraEventAction, SraPlacement } from '@/@types/graphql-types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { isProWebCountry } from '@/helpers/pro-web-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'TrailerModal',
  props: {
    clips: null,
    title: null,
    source: { default: 'modal' },
    trackingProperty: null,
    additionalContexts: { default: () => [] },
    playerAdditionalContexts: null,
    sponsoredAd: null,
    sraPlacement: null,
    hideTrailerAd: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { clips: MaybeRefOrGetter<TrailerClip[]>, title: TitleDetail, source: string, trackingProperty?: string, additionalContexts: SnowplowContext[], playerAdditionalContexts?: SnowplowContext[], sponsoredAd?: SponsoredAdFragment | null, sraPlacement?: SraPlacement, hideTrailerAd?: boolean };

const JWTVProFooter = () => import('@/pages/titleDetailV1/v1/hero/JWTVProFooter.vue');



// For this placement, we play the SR video once, then display the trailer
const hasSrVideoPlayedOnce = ref(false);

const options: YoutubePlayerOptions = { autoplay: 1, mute: 1 };

const { activeRoute } = storeToRefs(useRoutingStore());
const { trackSraEvent } = useBackendTracking();
const { isPremium } = storeToRefs(useUserStore());
const { titleId, titleName, titleObjectType } = useTitleDetailInfo(() => props.title);
const { webLocale } = storeToRefs(useLanguageStore());

watch(
	() => activeRoute,
	() => VFMHelper.close()
);

const isBackdropVideoSR = computed(() => {
	return props.sponsoredAd?.campaign && props.sraPlacement === SraPlacement.BackdropVideo;
});

const showSR = computed(() => isBackdropVideoSR.value && !hasSrVideoPlayedOnce.value);

const showTrailer = computed(() => !showSR.value || props.sponsoredAd?.holdoutGroup === true);

const trailerClip = computed(() => toValue(props.clips)[0]);

const showJWTVProFooter = computed(() => {
	return !isPremium.value && isProWebCountry(webLocale.value) && trailerClip.value?.provider === 'JUSTWATCH_TV';
});

const isYoutubePlayer = computed(() => {
	return trailerClip.value?.provider === 'YOUTUBE';
});

// Once SR videos played once, we can show the trailer
function handleTrailerVideoEnded(hasVideoEnded: boolean) {
	hasSrVideoPlayedOnce.value = hasVideoEnded;
}

const videoPlayer = computed(() => {
	return getTrailerComponent(trailerClip.value?.provider);
});

function handleCloseModal() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'trailer_modal_closed',
			label: titleId.value,
			property: 'title-header',
		},
		props.additionalContexts
	);

	// If there's a sponsored ad and the video has not been played once, we track the event
	if (props.sponsoredAd?.campaign && !hasSrVideoPlayedOnce.value) {
		trackSraEvent({
			action: SraEventAction.TrailerModalClosed,
			sponsoredAd: () => props.sponsoredAd ?? undefined,
		});
	}
}

function onTrack(eventName: string) {
	if (trailerClip.value?.provider !== 'YOUTUBE') {
		return;
	}
	const trackingMap = {
		buffering: 'youtube_buffering',
		playing: 'youtube_playing',
		paused: 'youtube_paused',
		ended: 'youtube_ended',
	};
	const category = trackingMap[eventName as keyof typeof trackingMap];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: titleObjectType.value.toLowerCase(),
				label: props.source,
				property: props.trackingProperty,
				nonInteraction: true,
				value: 1,
			},
			props.additionalContexts
		);
	}
}

return { __sfc: true,JWTVProFooter, props, hasSrVideoPlayedOnce, options, activeRoute, trackSraEvent, isPremium, titleId, titleName, titleObjectType, webLocale, isBackdropVideoSR, showSR, showTrailer, trailerClip, showJWTVProFooter, isYoutubePlayer, handleTrailerVideoEnded, videoPlayer, handleCloseModal, onTrack, NewDesignModal, SponsoredRecommendation }
}

})